import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import config from '../../config';
import { store } from '../../store';

function PharmacyUserDashboard({ onClose, phone }) {
    const [dashboardData, setDashboardData] = useState(null);
    const [collapseSections, setCollapseSections] = useState({});

    useEffect(() => {
        const fetchDashboardData = async () => {
            const token = store.getState()?.user?.token;
            const headers = token ? { authorization: token } : {};

            if (!phone) {
                console.warn("Phone number is not provided");
                return;
            }

            try {
                const url = `${config.pharmacyService}/v2/ops/fetch-pharmacy-user-dashboard?phone=${encodeURIComponent(phone)}`;
                const { data } = await axios.get(url, { headers });

                if (!data) {
                    alert("Dashboard data not available in response");
                    return;
                }

                setDashboardData(data);

                const sectionKeys = (data.sections || [])
                    .map(sec => sec?.title)
                    .filter(Boolean);

                const collapseInit = Object.fromEntries(
                    [...sectionKeys, 'Other Info'].map(k => [k, true])
                );

                setCollapseSections(collapseInit);
            } catch (err) {
                console.error("Error fetching dashboard data:", err);
                alert("Failed to fetch dashboard data");
                onClose();
            }
        };

        fetchDashboardData();
    }, [phone, onClose]);

    const getSectionStyle = (title) => {
        const hash = title.length % 3;
        return {
            backgroundColor: ['#e6f2ff', '#f0f8e2', '#fbeeee'][hash],
            borderColor: ['#007acc', '#82b366', '#d9534f'][hash],
        };
    };

    const toggleSection = (title) => {
        setCollapseSections(prev => ({ ...prev, [title]: !prev[title] }));
    };

    const renderDetail = (label, value) => {
        const formattedLabel = label.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());

        const isURL = (val) => {
            try { new URL(val); return true; } catch { return false; }
        };

        let displayValue;
        if (value === null || value === undefined || value === '') {
            displayValue = <span style={{ color: '#888' }}>N/A</span>;
        } else if (isURL(value)) {
            displayValue = <a href={value} target="_blank" rel="noopener noreferrer" style={{ color: '#0f4a11', fontWeight: 'bold', textDecoration: 'underline' }}>{value}</a>;
        } else {
            displayValue = <span style={{ color: '#0f4a11' }}>{value.toString()}</span>;
        }

        return (
            <div key={label} style={{ marginBottom: '8px' }}>
                <strong>{formattedLabel}:</strong> {displayValue}
            </div>
        );
    };

    const renderSection = (title, obj) => {
        if (!obj || (Array.isArray(obj) && obj.length === 0)) return null;
        const isCollapsed = collapseSections[title];
        const style = getSectionStyle(title);

        return (
            <div key={title} style={{
                marginBottom: '30px',
                padding: '20px',
                border: `1px solid ${style.borderColor}`,
                borderRadius: '10px',
                backgroundColor: style.backgroundColor
            }}>
                <h5 onClick={() => toggleSection(title)} style={{ cursor: 'pointer', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between' }}>
                    {title}
                    <span>{isCollapsed ? '▶' : '▼'}</span>
                </h5>
                {!isCollapsed && (
                    <div style={{ marginTop: '15px' }}>
                        {Array.isArray(obj) ? (
                            obj.map((item, idx) => (
                                <div key={idx} style={{ marginTop: '10px', padding: '15px', border: '1px solid #ccc', borderRadius: '8px', backgroundColor: '#fff' }}>
                                    {Object.entries(item).map(([subKey, subValue]) => renderDetail(subKey, subValue))}
                                </div>
                            ))
                        ) : (
                            Object.entries(obj).map(([key, value]) => {
                                if (typeof value === 'object' && !Array.isArray(value)) {
                                    return renderSection(key, value);
                                } else {
                                    return renderDetail(key, value);
                                }
                            })
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <Modal isOpen={true} toggle={onClose} style={{ maxWidth: '1400px', width: '100%' }}>
            <ModalHeader
                toggle={onClose}
                style={{
                    backgroundColor: '#eaf1f9',
                    color: '#2c3e50',
                    fontWeight: 'bold',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontSize: '1.1rem',
                    paddingTop: '15px',
                    paddingBottom: '15px',
                    textAlign: 'center',
                    gap: '25px',
                    flexWrap: 'nowrap'
                }}
            >
                <span><strong>User Pharmacy Dashboard</strong></span>
                <span style={{ fontSize: '18px', fontWeight: 'normal' }}>  |  </span>
                <span><strong>Phone : {phone} </strong></span>
            </ModalHeader>
            <ModalBody style={{ backgroundColor: '#fff', padding: '30px' }}>
                {dashboardData ? (
                    <>
                        {(dashboardData.sections || []).map((section, index) => {
                            if (section.wallet) {
                                return renderSection('Wallet', section.wallet[0]?.pharmacy || {});
                            } else if (section.title) {
                                return renderSection(section.title, section.card || []);
                            }
                            return null;
                        })}

                        {renderSection('Other Info', {
                            address: dashboardData.address,
                            showRxUploadOption: dashboardData.showRxUploadOption,
                            showExitingRxUploadOption: dashboardData.showExitingRxUploadOption,
                            isTataAIA: dashboardData.isTataAIA,
                            showSearchAndBook: dashboardData.showSearchAndBook,
                            isPolicyHolder: dashboardData.isPolicyHolder,
                        })}
                    </>
                ) : (
                    <div>Loading...</div>
                )}
            </ModalBody>
        </Modal>
    );
}

export default PharmacyUserDashboard;
